import React, { useState, useEffect, useContext, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { GlobalContext } from "../Context/GlobalContext";
import { images } from "../Assets/images";
import storage from "../Assets/js/Global";
import { Link, useNavigate } from "react-router-dom";
import { FileUp, ArrowLeft, ExternalLink, RefreshCw, AlertCircle, CameraOff, CheckCircle2 } from "lucide-react";

const loadJsQR = () =>
    new Promise((resolve, reject) => {
        if (window.jsQR) return resolve(window.jsQR);
        const script = document.createElement("script");
        script.src = "/jsQR.js";
        script.onload = () => resolve(window.jsQR);
        script.onerror = () => reject(new Error("Failed to load jsQR"));
        document.head.appendChild(script);
    });

const isValidFacescanUrl = (text) => {
    try {
        const url = new URL(text);
        // Matches /<dynamic-id>/download-app
        const pathRegex = /^\/[^\/]+\/download-app$/;
        return (
            pathRegex.test(url.pathname) &&
            url.searchParams.get("ref")
        );
    } catch {
        return false;
    }
};
const QrScanner = () => {
    storage.remove('signupData');
    storage.remove('verifiedEmail');
    const navigate = useNavigate();
    const { logoUrl, translatedLabels } = useContext(GlobalContext);
    const [scanResult, setScanResult] = useState(null);
    const [cameraError, setCameraError] = useState(null);   // camera-specific errors (shown softly)
    const [fileError, setFileError] = useState(null);        // file scan errors (shown as toast/inline)
    const [cameraLoading, setCameraLoading] = useState(true);
    const [fileLoading, setFileLoading] = useState(false);
    const [cameraAvailable, setCameraAvailable] = useState(true);
    const html5QrCodeRef = useRef(null);
    const scannerContainerId = "reader-container";
    const authLabel = translatedLabels?.auth;

    useEffect(() => {
        loadJsQR().catch(() => { });
    }, []);

    useEffect(() => {
        const checkAndStart = async () => {
            try {
                const cameras = await Html5Qrcode.getCameras().catch(() => []);
                if (cameras && cameras.length > 0) {
                    startCamera();
                } else {
                    setCameraAvailable(false);
                    setCameraLoading(false);
                }
            } catch {
                setCameraAvailable(false);
                setCameraLoading(false);
            }
        };
        checkAndStart();
        return () => {
            if (html5QrCodeRef.current?.isScanning) {
                html5QrCodeRef.current.stop().catch(() => { });
            }
        };
    }, []);

    const startCamera = async () => {
        setCameraError(null);
        setCameraLoading(true);
        setScanResult(null);
        setFileError(null);

        try {
            if (html5QrCodeRef.current?.isScanning) {
                await html5QrCodeRef.current.stop();
            }
        } catch { }

        const html5QrCode = new Html5Qrcode(scannerContainerId);
        html5QrCodeRef.current = html5QrCode;

        const config = { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 };

        const onScanSuccess = (decodedText) => {
            if (!isValidFacescanUrl(decodedText)) {
                setCameraError("Invalid QR Code. Please scan a valid Facescan signup QR.");
                html5QrCode.stop().then(() => setTimeout(() => startCamera(), 2000)).catch(() => { });
                return;
            }
            setScanResult(decodedText);
            try {
                const url = new URL(decodedText);
                const ref = url.searchParams.get("ref");
                if (ref) {
                    storage.set("refKey", ref);
                }
            } catch (e) { }
            html5QrCode.stop().catch(() => { });
            setTimeout(() => { navigate("/create-account-1"); }, 1000);
        };

        try {
            try {
                await html5QrCode.start({ facingMode: "environment" }, config, onScanSuccess);
            } catch {
                await html5QrCode.start({ facingMode: "user" }, config, onScanSuccess)
                    .catch(async () => html5QrCode.start({}, config, onScanSuccess));
            }
            setCameraLoading(false);
            setCameraAvailable(true);
        } catch {
            // Camera failed — don't show scary error, just show "no camera" placeholder
            setCameraAvailable(false);
            setCameraLoading(false);
        }
    };

    const decodeFileWithJsQR = (file) =>
        new Promise(async (resolve, reject) => {
            try {
                if (!file || !file.type.startsWith("image/")) {
                    reject(new Error("InvalidFileType"));
                    return;
                }

                const jsQR = await loadJsQR();
                if (!jsQR) {
                    reject(new Error("JsQRNotLoaded"));
                    return;
                }

                const img = new Image();
                const objectUrl = URL.createObjectURL(file);

                img.onload = () => {
                    try {
                        const canvas = document.createElement("canvas");
                        const ctx = canvas.getContext("2d", { willReadFrequently: true });

                        if (!ctx) {
                            URL.revokeObjectURL(objectUrl);
                            reject(new Error("CanvasContextError"));
                            return;
                        }

                        canvas.width = img.naturalWidth || img.width;
                        canvas.height = img.naturalHeight || img.height;

                        if (!canvas.width || !canvas.height) {
                            URL.revokeObjectURL(objectUrl);
                            reject(new Error("InvalidImageDimensions"));
                            return;
                        }

                        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                        let imageData;
                        try {
                            imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                        } catch {
                            URL.revokeObjectURL(objectUrl);
                            reject(new Error("ImageDataReadError"));
                            return;
                        }

                        if (!imageData || !imageData.data) {
                            URL.revokeObjectURL(objectUrl);
                            reject(new Error("InvalidImageData"));
                            return;
                        }

                        let result = jsQR(
                            imageData.data,
                            imageData.width,
                            imageData.height,
                            { inversionAttempts: "dontInvert" }
                        );

                        if (!result) {
                            result = jsQR(
                                imageData.data,
                                imageData.width,
                                imageData.height,
                                { inversionAttempts: "onlyInvert" }
                            );
                        }

                        URL.revokeObjectURL(objectUrl);

                        if (result?.data) {
                            resolve(result.data);
                        } else {
                            reject(new Error("NotFoundException"));
                        }
                    } catch {
                        URL.revokeObjectURL(objectUrl);
                        reject(new Error("ProcessingError"));
                    }
                };

                img.onerror = () => {
                    URL.revokeObjectURL(objectUrl);
                    reject(new Error("ImageLoadError"));
                };

                img.src = objectUrl;
            } catch (e) {
                reject(e);
            }
        });

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        event.target.value = "";

        setFileError(null);
        setFileLoading(true);
        setScanResult(null);

        if (html5QrCodeRef.current?.isScanning) {
            try { await html5QrCodeRef.current.stop(); } catch { }
        }

        try {
            const decodedText = await decodeFileWithJsQR(file);

            if (!isValidFacescanUrl(decodedText)) {
                setFileError("Invalid QR Code. Please upload a valid QR.");
                setFileLoading(false);
                return;
            }

            setScanResult(decodedText);
            try {
                const url = new URL(decodedText);
                const ref = url.searchParams.get("ref");
                if (ref) {
                    storage.set("refKey", ref);
                }
            } catch (e) { }
            setFileLoading(false);
            setTimeout(() => { navigate("/create-account-1"); }, 1000);
        } catch (err) {
            const msg = err?.message || "";
            if (msg === "NotFoundException") {
                setFileError("No QR code found in this image. Please try a clearer image.");
            } else if (msg === "ImageLoadError") {
                setFileError("Could not load the image. Please try a different file.");
            } else {
                setFileError("Unable to read the image. Please try again.");
            }
            setFileLoading(false);
        }
    };

    return (
        <section id="qrScannerPage" className="login-section-bg" style={{ minHeight: "100vh", background: "#f8f9fa" }}>
            <div className="container-main justify-content-between" style={{ maxWidth: "480px", margin: "0 auto", padding: "16px" }}>

                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <Link to="/" className="text-dark p-2" style={{ marginLeft: "-8px" }}>
                        <ArrowLeft size={24} />
                    </Link>
                    <div className="app-logo" style={{ width: "200px", height: "70px" }}>
                        <img
                            src={logoUrl || images.appLogo}
                            alt="app-logo"
                            style={{ width: "100%", height: "100%", objectFit: "contain" }}
                            onError={(e) => { e.target.src = images.appLogo; }}
                        />
                    </div>
                    <div style={{ width: "40px" }} />
                </div>

                {/* Scanner Card */}
                <div className=" border-0 rounded-4 overflow-hidden mb-3">


                    <div className="card-body p-3 pt-0">
                        {/* Scanner viewport */}
                        <div
                            className="position-relative rounded-4 overflow-hidden"
                            style={{
                                height: "300px",
                                background: "linear-gradient(135deg, #1a1a1c 0%, #000000 100%)",
                            }}
                        >
                            {/* Camera div — always in DOM so html5-qrcode can attach */}
                            <div
                                id={scannerContainerId}
                                style={{
                                    width: "100%", height: "100%", overflow: "hidden",
                                    display: cameraAvailable ? "block" : "none"
                                }}
                            />

                            {/* No camera placeholder */}
                            {!cameraAvailable && !scanResult && (
                                <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center text-white">
                                    <CameraOff size={52} className="mb-3" style={{ opacity: 0.4 }} />
                                    <p className="mb-0 small text-center text-white px-4" style={{ opacity: 0.5 }}>
                                        {authLabel?.camera_unavailable?.label_value || "Camera unavailable."}<br />{authLabel?.upload_from_gallery?.label_value || "Use \"Upload from Gallery\" below."}
                                    </p>
                                </div>
                            )}

                            {/* Camera loading overlay */}
                            {cameraAvailable && cameraLoading && (
                                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                    style={{ background: "rgba(0,0,0,0.7)", zIndex: 20 }}>
                                    <div className="text-center text-white">
                                        <RefreshCw size={40} className="mb-2 animate-spin" style={{ color: "#4f8ef7" }} />
                                        <p className="mb-0 small">{authLabel?.starting_camera?.label_value || "Starting camera…"}</p>
                                    </div>
                                </div>
                            )}

                            {/* Invalid QR scanned via camera */}
                            {cameraError && !cameraLoading && (
                                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center"
                                    style={{ background: "rgba(0,0,0,0.75)", zIndex: 20, backdropFilter: "blur(6px)" }}>
                                    <AlertCircle size={40} className="mb-2" style={{ color: "#f66" }} />
                                    <p className="text-white text-center small px-4">{cameraError}</p>
                                </div>
                            )}

                            {/* Success overlay */}
                            {scanResult && (
                                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center"
                                    style={{ background: "rgba(0,0,0,0.75)", zIndex: 20, backdropFilter: "blur(6px)", borderRadius: "1rem" }}>
                                    <CheckCircle2 size={52} className="mb-3" style={{ color: "#4caf50" }} />
                                    <h6 className="text-white font-700 mb-1">{authLabel?.qr_detected?.label_value || "QR Detected!"}</h6>
                                    <p className="text-white-50 small text-center px-4 mb-0" style={{ fontSize: "11px", wordBreak: "break-all" }}>{scanResult}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* File upload button */}
                <div className="d-grid gap-2">
                    <div className="position-relative">
                        <input
                            type="file"
                            accept="image/*"
                            className="position-absolute opacity-0 w-100 h-100"
                            onChange={handleFileUpload}
                            style={{ zIndex: 10, cursor: "pointer" }}
                        />
                        <button
                            className="btn w-100 d-flex align-items-center justify-content-center gap-2 py-3 rounded-3 text-decoration-underline"
                            disabled={fileLoading}
                        >
                            {fileLoading
                                ? <><RefreshCw size={18} className="animate-spin text-primary" /><span className="font-600"> {authLabel?.scanning_image?.label_value || "Scanning image…"}</span></>
                                : <><FileUp size={20} className="text-primary" /><p className="text-muted small mb-0">
                                    {cameraAvailable ? authLabel?.position_qr_code?.label_value || "Position the QR code within the frame" : authLabel?.upload_qr_code?.label_value || "Upload a QR code image to scan"}
                                </p></>
                            }
                        </button>
                    </div>

                    {/* File error shown inline below button — not as scary overlay */}
                    {fileError && (
                        <div className="d-flex align-items-start gap-2 px-3 py-2 rounded-3"
                            style={{ background: "#fff3f3", border: "1px solid #fcc", color: "#c00" }}>
                            <AlertCircle size={16} style={{ marginTop: "2px", flexShrink: 0 }} />
                            <span style={{ fontSize: "13px" }}>{fileError}</span>
                        </div>
                    )}
                </div>
            </div>

            <style jsx="true">{`
                #${scannerContainerId} {
                    overflow: hidden !important;
                    height: 300px !important;
                    border-radius: 12px !important;
                }
                #${scannerContainerId} video,
                #${scannerContainerId} canvas,
                #${scannerContainerId} img {
                    width: 100% !important;
                    height: 100% !important;
                    object-fit: cover !important;
                }
                #${scannerContainerId} > div:first-child {
                    height: 100% !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                }
                #${scannerContainerId} div[style*="text-align: center"],
                #${scannerContainerId} b,
                #${scannerContainerId} span {
                    display: none !important;
                }
                .animate-spin {
                    animation: spin 1.2s linear infinite;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </section>
    );
};

export default QrScanner;
